import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, ReplaySubject, shareReplay } from 'rxjs';

import { RevisionLiabilityClient } from '../client/revision-liability.client';
import { RevisionLiability } from '../model/design-revision-reason';

/**
 * Caches the revision liabilities so every picker and label renders the same list the settings page
 * maintains, instead of a hard-coded constant that cannot see a liability QC added.
 *
 * The cached list always includes retired liabilities: a historical judgement has to keep rendering its
 * label. Callers offering a *new* selection use {@link active$}.
 */
@Injectable({ providedIn: 'root' })
export class RevisionLiabilityStore {
    private readonly _client = inject(RevisionLiabilityClient);
    private readonly _source$ = new ReplaySubject<RevisionLiability[]>(1);
    private _value$?: Observable<RevisionLiability[]>;

    /** Every liability, retired ones included. Use for rendering existing data. */
    get value$(): Observable<RevisionLiability[]> {
        if (this._value$ == null) {
            // Derived from the subject rather than from one request, so a later refresh reaches
            // everyone already subscribed.
            this._value$ = this._source$.pipe(shareReplay(1));
            this.refresh();
        }
        return this._value$;
    }

    /** Only the liabilities still in use. Use for anything that offers a new selection. */
    get active$(): Observable<RevisionLiability[]> {
        return this.value$.pipe(map(liabilities => liabilities.filter(x => x.isActive)));
    }

    /**
     * Re-reads the list and emits the **new** value, so a caller that has just changed a liability can
     * await the result. Returning the subject instead would replay the stale value immediately and hand
     * back what the caller was trying to replace.
     *
     * Never throws: a picker that cannot load the list should render empty rather than hang forever on
     * a ReplaySubject that never emits.
     */
    refresh(): Observable<RevisionLiability[]> {
        const request = this._client.list(true).pipe(
            catchError(() => of<RevisionLiability[]>([])),
            shareReplay(1)
        );
        request.subscribe(liabilities => this._source$.next(liabilities));
        return request;
    }
}
